// The public trust page at /trust, for anyone, signed in or not: how LAMP's agents work, who processes
// data, how LAMP keeps it safe and what the transparency reports say. Its content is built from
// docs/trust/ with the web app and read from LAMP's own origin; nothing comes from a third party. In a
// production Cell the api serves it only once every section is lawyer-reviewed; until then it shows DRAFT.
import { useEffect, useState } from "react";
import { formatDate } from "../i18n/format.js";
import type { Language } from "../i18n/language.js";
import { CATALOGUES, useLanguage, useWords, type Words } from "../i18n/words.js";
import {
  TRUST_CONTENT_FILE,
  type Block,
  type Inline,
  type ReportSection,
  type TrustContent,
  type TrustSection,
} from "./content.js";

export type LoadTrust = () => Promise<TrustContent | undefined>;

/** The page's content from the api, or undefined when the Cell does not publish it. */
export const loadTrust: LoadTrust = async () => {
  const response = await fetch(`/${TRUST_CONTENT_FILE}`, { credentials: "omit" });
  return response.ok ? ((await response.json()) as TrustContent) : undefined;
};

/** "2026-09-27" reads "27 September 2026" (or "27 सितंबर 2026"). */
export const longDate = (date: string, language: Language = "en"): string =>
  formatDate(`${date}T00:00:00Z`, language, { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });

/** 42 reads "42 minutes"; 245 reads "4 hours 5 minutes". */
export function duration(minutes: number, w: Words = CATALOGUES.en): string {
  const hours = Math.floor(minutes / 60);
  return w.trustDuration(hours, Math.round(minutes - hours * 60));
}

function Inlines({ inlines }: { inlines: readonly Inline[] }) {
  return (
    <>
      {inlines.map((inline, i) =>
        inline.kind === "strong" ? (
          <strong key={i}>{inline.text}</strong>
        ) : inline.kind === "link" ? (
          <a key={i} href={inline.href}>
            {inline.text}
          </a>
        ) : (
          inline.text
        ),
      )}
    </>
  );
}

function Blocks({ blocks }: { blocks: readonly Block[] }) {
  return (
    <>
      {blocks.map((block, i) =>
        block.kind === "heading" ? (
          block.level === 3 ? (
            <h3 key={i}>
              <Inlines inlines={block.inlines} />
            </h3>
          ) : (
            <h4 key={i}>
              <Inlines inlines={block.inlines} />
            </h4>
          )
        ) : block.kind === "list" ? (
          <ul key={i}>
            {block.items.map((item, j) => (
              <li key={j}>
                <Inlines inlines={item} />
              </li>
            ))}
          </ul>
        ) : (
          <p key={i}>
            <Inlines inlines={block.inlines} />
          </p>
        ),
      )}
    </>
  );
}

/** One section of a transparency report: its figures, its table and the changes it lists, with their source. */
function ReportPart({ section }: { section: ReportSection }) {
  const w = useWords();
  const { language } = useLanguage();
  const { title, source, figures, table, changes, note } = section;
  return (
    <section aria-label={title}>
      <h4>{title}</h4>
      <p className="small muted">{w.trustReportSource(source)}</p>
      {note ? <p>{note}</p> : null}
      {figures.length ? (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th scope="col">{w.trustReportFigure}</th>
                <th scope="col">{w.trustReportForPeriod}</th>
              </tr>
            </thead>
            <tbody>
              {figures.map((figure) => (
                <tr key={figure.label}>
                  <th scope="row">{figure.label}</th>
                  <td>{figure.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {table?.rows.length ? (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                {table.columns.map((column) => (
                  <th key={column} scope="col">
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((row, i) => (
                <tr key={i}>
                  {row.map((cell, j) =>
                    j === 0 ? (
                      <th key={j} scope="row">
                        {cell}
                      </th>
                    ) : (
                      <td key={j}>{cell}</td>
                    ),
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {changes ? (
        changes.length ? (
          <ul>
            {changes.map((change) => (
              <li key={`${change.date}-${change.what}`}>
                <time dateTime={change.date}>{longDate(change.date, language)}</time>: {change.what}
              </li>
            ))}
          </ul>
        ) : (
          <p>{w.trustReportNoChanges}</p>
        )
      ) : null}
    </section>
  );
}

/** What each section shows after its own words: the drill's recovery, or the published reports. */
function SectionData({ section, content }: { section: TrustSection; content: TrustContent }) {
  const w = useWords();
  const { language } = useLanguage();
  const { recovery, reports } = content;
  if (section.id === "security" && recovery) {
    return (
      <>
        <h3>{w.trustRecoveryHeading}</h3>
        <p>{w.trustRecoveryDrill(longDate(recovery.drilledAt, language), recovery.tested)}</p>
        <ul>
          <li>
            {w.trustRecoveryTime} <strong>{duration(recovery.recoveryTimeMinutes, w)}</strong>
          </li>
          <li>
            {w.trustRecoveryPoint} <strong>{duration(recovery.recoveryPointMinutes, w)}</strong>
          </li>
        </ul>
      </>
    );
  }
  if (section.id === "transparency-reports") {
    if (!reports.length) return <p className="muted">{w.trustNoReport}</p>;
    return (
      <>
        {reports.map((report) => {
          const period = w.trustReportPeriod(longDate(report.from, language), longDate(report.to, language));
          return (
            <article key={`${report.from}-${report.to}`} aria-label={w.trustReportFor(period)}>
              <h3>{period}</h3>
              <p className="small muted">
                {w.trustReportGenerated(longDate(report.generatedOn, language), report.publishedBy)}
              </p>
              <p>{report.follows}</p>
              <p>
                {report.smallCounts} {w.trustReportBusinesses(report.tenants)}
              </p>
              {report.sections.map((section) => (
                <ReportPart key={section.id} section={section} />
              ))}
            </article>
          );
        })}
      </>
    );
  }
  return null;
}

export function Trust({ load = loadTrust }: { load?: LoadTrust }) {
  const w = useWords();
  const { language } = useLanguage();
  const [content, setContent] = useState<TrustContent | null | undefined>(undefined);

  useEffect(() => {
    let live = true;
    load().then(
      (c) => live && setContent(c ?? null),
      () => live && setContent(null),
    );
    return () => {
      live = false;
    };
  }, [load]);

  if (content === undefined)
    return (
      <p role="status" className="loading">
        {w.loading}
      </p>
    );
  if (content === null)
    return (
      <main id="content" className="public-page">
        <h1>{w.trustUnpublishedTitle}</h1>
        <p>{w.trustUnpublished}</p>
        <p>
          <a href="/">{w.trustBack}</a>
        </p>
      </main>
    );

  const draft = content.sections.some((s) => s.review === "draft");
  return (
    <main id="content" className="public-page trust">
      {draft ? (
        <p role="note" className="trust-draft">
          <strong>{w.trustDraft}</strong> {w.trustDraftBody}
        </p>
      ) : null}
      <h1>{w.trustTitle}</h1>
      <nav aria-label={w.trustOnThisPage}>
        <ul>
          {content.sections.map((s) => (
            <li key={s.id}>
              <a href={`#${s.id}`}>{s.title}</a>
            </li>
          ))}
          {content.incidents.length ? (
            <li>
              <a href="#recent-incidents">{w.trustRecentIncidents}</a>
            </li>
          ) : null}
        </ul>
      </nav>
      {content.sections.map((section) => (
        <section key={section.id} id={section.id} aria-labelledby={`${section.id}-title`}>
          <h2 id={`${section.id}-title`} className="sectionh">
            {section.title}
            {section.review === "draft" ? <span className="small muted"> · {w.trustSectionDraft}</span> : null}
          </h2>
          <Blocks blocks={section.blocks} />
          <SectionData section={section} content={content} />
        </section>
      ))}
      {content.incidents.length ? (
        <section id="recent-incidents" aria-labelledby="recent-incidents-title">
          <h2 id="recent-incidents-title" className="sectionh">
            {w.trustRecentIncidents}
          </h2>
          <ul className="trust-incidents">
            {content.incidents.map((incident) => (
              <li key={`${incident.date}-${incident.title}`}>
                <h3>
                  <time dateTime={incident.date}>{longDate(incident.date, language)}</time>: {incident.title}
                </h3>
                <Blocks blocks={incident.blocks} />
                <p>
                  <a href={incident.postmortem}>{w.trustPostmortem}</a>
                </p>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      <p>
        <a href="/">{w.trustBack}</a>
      </p>
    </main>
  );
}
